import { mapActions, mapGetters } from 'vuex';
import { GoogleMap, AdvancedMarker, MarkerCluster, Polygon, Polyline } from 'vue3-google-map';
import { PolyUtil } from 'node-geometry-library';
import { debounce } from 'lodash';
import { loadGMap } from '@/common/utilities/nojq/_load.map.js';

/* Markers */
import mvtPropertyMarker from './mvt-propertymarker.vue';
import mvtNewMapOptions from './mvt-mapoptionspanel.vue';
import mvtPoiMarker from './mvt-poimarker.vue';
import mvtSchoolMarker from './mvt-schoolmarker.vue';
import { activeMapStyle, inactiveMapStyle } from './mapStyles.js';
import isNumber from 'lodash/isNumber.js';

function delayFn(delay) {
    // Fall back to yielding with requestAnimationFrame.
    return new Promise((resolve) => {
        if (delay) {
            requestAnimationFrame(() => {
                resolve();
            });
        } else {
            resolve();
        }
    });
}
const mapTypesDict = ['activeMap', 'satellite', 'roadmap', 'hybrid', 'terrain'];
const outerMapBounds = [
    { lat: 85, lng: 180 },
    { lat: 85, lng: 90 },
    { lat: 85, lng: 0 },
    { lat: 85, lng: -90 },
    { lat: 85, lng: -180 },
    { lat: 0, lng: -180 },
    { lat: -85, lng: -180 },
    { lat: -85, lng: -90 },
    { lat: -85, lng: 0 },
    { lat: -85, lng: 90 },
    { lat: -85, lng: 180 },
    { lat: 0, lng: 180 },
    { lat: 85, lng: 180 },
];

export const pinType = {
    property: 'property',
    poi: 'poi',
    school: 'school',
};

export default {
    name: 'mvtMap',
    inject: ['$eventBus'],
    components: {
        GoogleMap,
        AdvancedMarker,
        MarkerCluster,
        Polygon,
        Polyline,
        mvtPropertyMarker,
        mvtPoiMarker,
        mvtSchoolMarker,
        mvtNewMapOptions,
    },
    props: {
        listings: {
            type: Array,
            default: [],
        },
        pois: {
            type: Array,
            default: [],
        },
        schools: {
            type: Array,
            default: [],
        },
        disableBoundary: {
            type: Boolean,
            default: false,
        },
        boundaryIndexId: {
            type: String,
            default: null,
        },
        inverseBoundary: {
            type: Boolean,
            default: false,
        },
        boundaryTheme: {
            type: String,
            default: 'gray',
        },
        zoomControl: {
            type: Boolean,
            default: false,
        },
        coordinates: {
            type: Object,
            default: null,
        },
        disableMarkerPopups: {
            type: Boolean,
            default: false,
        },
        gestureHandling: {
            type: String,
            default: 'greedy',
        },
        center: {
            type: Object,
            default: null,
        },
        zoom: {
            type: Number,
            default: 15,
        },
        autoSelectMarker: {
            type: String,
            default: null,
        },
        popMapTypeId: {
            type: Number,
            default: 0,
        },
    },
    emits: ['boundsChanged', 'zoomChanged', 'centerChanged', 'markerSelected', 'markerUnselect', 'ready'],
    data() {
        return {
            hasTouch: false,
            isMounted: false,
            draggable: true,
            isDragging: false,
            hasMapChanged: false,
            disableMapChangeEvent: false,
            activeMapType: mapTypesDict[this.popMapTypeId] || 'activeMap',
            activeLayer: null,
            gmapLayerRef: null,
            streetViewRef: null,
            streetViewAvailable: false,
            /* Boundary */
            initialBoundaryFetched: false,
            boundaryDisabled: false,
            boundaryPath: [],
            boundaryPolygonStyle: {
                gray: {
                    strokeColor: '#a4a4a4',
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    fillColor: '#a4a4a4',
                    fillOpacity: 0.25,
                },
                blue: {
                    strokeColor: '#466B9C',
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    fillColor: '#466B9C',
                    fillOpacity: 0.2,
                },
            },
            boundaryPolylineStyle: {
                strokeOpacity: 0,
                fillOpacity: 0,
                icons: [
                    {
                        icon: {
                            // circle path, alternative to google.maps.SymbolPath.CIRCLE
                            path: 'M 0,0 m -5,0 a 5,5 0 1,0 10,0 a 5,5 0 1,0 -10,0',
                            fillOpacity: 1,
                            fillColor: '#a4a4a4',
                            strokeOpacity: 0,
                            scale: 0.35,
                        },
                        offset: '0',
                        repeat: '5px',
                    },
                ],
            },
            isBoundsInMapView: true,
            /* Drawing */
            freeDrawEnabled: false,
            drawingLayerRef: null,
            drawnPath: [],
            drawnBounds: null,
            apiPromise: null,
        };
    },
    computed: {
        selectedKey() {
            if (this.autoSelectMarker && this.autoSelectMarker.lat && this.autoSelectMarker.lng && this.autoSelectMarker.type) {
                return `${this.autoSelectMarker.type}_${this.autoSelectMarker.lat}_${this.autoSelectMarker.lng}`;
            }
            return '';
        },
        /* Markers */
        listingMarkers() {
            const groupedListings = this.listings.reduce((accumulator, listing) => {
                if (!listing.geo.lat || !listing.geo.lng) return accumulator; // skip if no lat/lng
                const key = `${pinType.property}_${listing.geo.lat}_${listing.geo.lng}`;
                if (!accumulator.hasOwnProperty(key)) {
                    accumulator[key] = {
                        key,
                        position: { lat: listing.geo.lat, lng: listing.geo.lng },
                        data: [listing],
                        autoSelect: key === this.selectedKey,
                        isHomePin: listing.isHomePin,
                    };
                } else {
                    accumulator[key].data.push(listing);
                    if (this.selectedId === listing.propertyId) {
                        accumulator[key].autoSelect = true;
                    }
                }
                return accumulator;
            }, {});
            return Object.keys(groupedListings).map((key) => groupedListings[key]);
        },
        poiMarkers() {
            const groupedPois = this.pois.reduce((accumulator, poi) => {
                const key = `${pinType.poi}_${poi.lat}_${poi.lon}`;
                if (!poi.lat || !poi.lon) return accumulator; // skip if no lat/lng
                if (!accumulator.hasOwnProperty(key)) {
                    accumulator[key] = {
                        key,
                        position: { lat: poi.lat, lng: poi.lon },
                        data: poi,
                        autoSelect: key === this.selectedKey,
                    };
                }
                return accumulator;
            }, {});
            return Object.keys(groupedPois).map((key) => groupedPois[key]);
        },
        schoolMarkers() {
            const groupedSchools = this.schools.reduce((accumulator, school) => {
                const key = `${pinType.school}_${school.lat}_${school.lon}`;
                if (!school.lat || !school.lon) return accumulator; // skip if no lat/lng
                if (!accumulator.hasOwnProperty(key)) {
                    accumulator[key] = {
                        key,
                        position: { lat: school.lat, lng: school.lon },
                        data: school,
                        autoSelect: key === this.selectedKey,
                    };
                }
                return accumulator;
            }, {});
            return Object.keys(groupedSchools).map((key) => groupedSchools[key]);
        },
        homePinMarker() {
            const homePin = this.listingMarkers.find((listing) => listing.isHomePin);
            return homePin ? homePin : null;
        },
        /* Boundary */
        boundaryPolygon() {
            // This creates an inverse boundary on the outside of
            // the geo defined boundary (to be filled in)
            if (!this.boundaryPath.length) return null;
            return !this.inverseBoundary ? [outerMapBounds].concat(this.boundaryPath) : this.boundaryPath;
        },
        boundaryPolylines() {
            if (!this.boundaryPath.length) return null;
            return this.boundaryPath;
        },
        /* Drawning */
        drawnPolygon() {
            if (!this.drawnPath.length) return null;
            return [outerMapBounds, this.drawnPath];
        },
        /* Map Type */
        mapType() {
            if (this.activeLayer === 'biking' || this.activeLayer === 'transit') {
                return 'roadmap';
            }
            return this.activeMapType;
        },
        /* Activity Status */
        isMapReady() {
            if (this.isMounted) {
                return this.$refs.mapRef && this.$refs.mapRef.ready;
            }
            return false;
        },
        isInitialBoundaryReady() {
            return this.isMapReady && this.initialBoundaryFetched;
        },
        hasActiveBoundary() {
            return !this.boundaryDisabled && !!this.boundaryPolygon && !this.freeDrawEnabled && !this.hasActiveDrawing;
        },
        hasInactiveBoundary() {
            return (this.boundaryDisabled && !!this.boundaryPolylines) || this.freeDrawEnabled;
        },
        hasActiveDrawing() {
            return !!this.drawnPolygon;
        },
        hasActiveStreetView() {
            return !!(this.streetViewRef && this.streetViewRef?.getVisible());
        },
    },
    methods: {
        ...mapActions('glb', ['getBoundaries']),
        /* Map Styles */
        initializeMapStyles() {
            const api = this.$refs.mapRef.api;
            const gmap = this.$refs.mapRef.map;

            const inactiveStyle = new api.StyledMapType(inactiveMapStyle, { name: 'Inactive Map' });
            const activeStyle = new api.StyledMapType(activeMapStyle, { name: 'Active Map' });

            gmap.mapTypes.set('inactiveMap', inactiveStyle);
            gmap.mapTypes.set('activeMap', activeStyle);
        },
        /* Drawing Methods */
        createDrawingManager() {
            const api = this.$refs.mapRef.api;
            const gmap = this.$refs.mapRef.map;

            if (this.drawingLayerRef) {
                this.drawingLayerRef.setMap(null);
            }

            this.drawingLayerRef = new api.drawing.DrawingManager({
                map: gmap,
                drawingMode: api.drawing.OverlayType.RECTANGLE,
                drawingControl: false,
                drawingControlOptions: {
                    position: api.ControlPosition.TOP_CENTER,
                    drawingModes: [api.drawing.OverlayType.RECTANGLE],
                },
                rectangleOptions: {
                    fillColor: 'transparent',
                    strokeWeight: 2,
                    clickable: false,
                },
            });

            api.event.addListener(this.drawingLayerRef, 'rectanglecomplete', (rectangle) => {
                // Get the bounds of the drawn rectangle
                const rectangleBounds = rectangle.getBounds();

                // Extract the four corners of the rectangle
                const ne = rectangleBounds.getNorthEast();
                const sw = rectangleBounds.getSouthWest();
                const nw = { lat: ne.lat(), lng: sw.lng() }; // North-West corner
                const se = { lat: sw.lat(), lng: ne.lng() }; // South-East corner

                this.drawnBounds = rectangleBounds;

                this.drawnPath = [
                    nw, // NW corner
                    ne, // NE corner
                    se, // SE corner
                    sw, // SW corner
                    nw, // Closing the path
                ];

                gmap.fitBounds(rectangleBounds);

                rectangle.setMap(null);
                this.destroyDrawingManager();
            });
        },
        getBounds() {
            const gmap = this.$refs.mapRef.map;
            const bounds = gmap.getBounds();
            return bounds;
        },
        destroyDrawingManager() {
            if (this.drawingLayerRef) {
                this.drawingLayerRef.setMap(null);
            }
            this.drawingLayerRef = null;
            this.freeDrawEnabled = false;
        },
        /* Boundary Methods */
        async fetchBoundaryPath() {
            this.initialBoundaryFetched = false;
            let newBoundary = [];

            if (this.boundaryIndexId) {
                this.clearDrawing();
                const boundaryData = await this.getBoundaries(this.boundaryIndexId);
                if (boundaryData && Array.isArray(boundaryData)) {
                    newBoundary = boundaryData.map((encodedPath) => {
                        return PolyUtil.decode(encodedPath);
                    });
                }
            }
            this.boundaryPath = newBoundary;
            this.initialBoundaryFetched = true;
        },
        fitBounds(coordinateArray, zoomLevel) {
            this.disableMapChangeEvent = true;

            const api = this.$refs.mapRef.api;
            const gmap = this.$refs.mapRef?.map;

            delayFn(!gmap).then(() => {
                const mapDimensions = {
                    width: gmap.getDiv().offsetWidth,
                    height: gmap.getDiv().offsetHeight,
                };
                const bounds = new api.LatLngBounds();
                coordinateArray.forEach(({ lat, lng }) => {
                    bounds.extend(new api.LatLng(lat, lng));
                });

                gmap.fitBounds(bounds);

                const zoom = zoomLevel ? zoomLevel : this.getBoundsZoomLevel(bounds, mapDimensions);

                api.event.addListenerOnce(gmap, 'bounds_changed', function (event) {
                    if (isNumber(zoom) && gmap.getZoom() !== zoom) {
                        gmap.setZoom(zoom);
                    }
                });
            });
        },
        setCenter(lat, lng, zoom) {
            this.disableMapChangeEvent = true;
            const api = this.$refs.mapRef.api;
            const gmap = this.$refs.mapRef.map;
            gmap.setCenter(new api.LatLng(lat, lng), zoom || this.zoom);
        },
        panTo(lat, lng) {
            this.disableMapChangeEvent = true;
            const api = this.$refs.mapRef.api;
            const gmap = this.$refs.mapRef.map;
            gmap.panTo(new api.LatLng(lat, lng));
        },
        getBoundsZoomLevel(bounds, mapDim) {
            const WORLD_DIM = { height: 256, width: 256 };
            const ZOOM_MAX = 21;

            const latRad = (lat) => {
                var sin = Math.sin((lat * Math.PI) / 180);
                var radX2 = Math.log(Math.abs((1 + sin) / (1 - sin))) / 2;
                return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
            };
            const zoom = (mapPx, worldPx, fraction) => {
                return Math.floor(Math.log(Math.abs(mapPx / worldPx / fraction)) / Math.LN2);
            };
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;
            const lngDiff = ne.lng() - sw.lng();
            const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;
            const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
            const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);
            return Math.min(latZoom, lngZoom, ZOOM_MAX);
        },
        evaluateBoundsInMapView() {
            const gmap = this.$refs.mapRef?.map;
            if (gmap) {
                const currentMapBounds = gmap.getBounds();
                if (currentMapBounds) {
                    if (this.hasActiveDrawing) {
                        for (let point = 0; point < this.drawnPath.length; point++) {
                            const coords = this.drawnPath[point];
                            if (currentMapBounds.contains(coords)) {
                                this.isBoundsInMapView = true;
                                return;
                            }
                        }
                    } else if (this.hasActiveBoundary) {
                        for (let set = 0; set < this.boundaryPath.length; set++) {
                            for (let point = 0; point < this.boundaryPath[set].length; point++) {
                                const coords = this.boundaryPath[set][point];
                                if (currentMapBounds.contains(coords)) {
                                    this.isBoundsInMapView = true;
                                    return;
                                }
                            }
                        }
                    }
                }
            }
            this.isBoundsInMapView = false;
        },
        resetMapPosition() {
            this.disableMapChangeEvent = true;
            this.boundaryDisabled = this.disableBoundary;

            if (this.homePinMarker) {
                this.setCenter(this.homePinMarker.position.lat, this.homePinMarker.position.lng, 16);
            } else if (this.coordinates?.ne && this.coordinates?.sw) {
                const ne = this.coordinates.ne;
                const sw = this.coordinates.sw;
                const nw = { lat: ne.lat, lng: sw.lng };
                const se = { lat: sw.lat, lng: ne.lng };
                this.fitBounds([nw, ne, se, sw, nw], this.coordinates?.zoom);
            } else if (this.boundaryPath.length > 0) {
                const boundaryPathBounds = this.boundaryPath.flat(1);
                this.fitBounds(boundaryPathBounds);
            } else if (this.listingMarkers.length > 0) {
                const listingBounds = this.listingMarkers.map((listing) => ({ lat: listing.position.lat, lng: listing.position.lng }));
                this.fitBounds(listingBounds);
            } else if (this.center && this.center.lat && this.center.lng) {
                this.setCenter(this.center.lat, this.center.lng, this.zoom);
            } else {
                this.disableMapChangeEvent = false;
            }
        },
        /* Map Events */
        onDragStart() {
            this.isDragging = true;
        },
        onDragEnd() {
            this.isDragging = false;
            this.hasMapChanged = true;
        },
        onCenterChanged() {
            const gmap = this.$refs.mapRef.map;
            if (!gmap || this.disableMapChangeEvent) {
                return;
            }
            this.hasMapChanged = true;
            this.$emit('centerChanged', gmap);
        },
        onZoomChanged() {
            const gmap = this.$refs.mapRef.map;
            if (!gmap || this.disableMapChangeEvent) {
                return;
            }
            this.hasMapChanged = true;
            this.$emit('zoomChanged', gmap);
        },
        onIdle() {
            if (this.hasMapChanged && !this.isDragging) {
                this.debouncedOnBoundsChanged();
                this.evaluateBoundsInMapView();
            } else {
                this.disableMapChangeEvent = false;
            }
        },
        onBoundsChanged() {
            if (!this.disableMapChangeEvent) {
                const gmap = this.$refs.mapRef?.map;
                if (gmap) {
                    this.$emit('boundsChanged', {
                        map: gmap,
                        bounds: this.drawnBounds || gmap.getBounds(),
                        zoom: gmap.getZoom() || null,
                        filteredByBoundary: !((this.hasActiveDrawing && !this.isBoundsInMapView) || this.hasActiveBoundary),
                    });
                    this.hasMapChanged = false;
                }
            }
            this.disableMapChangeEvent = false;
        },
        debouncedOnBoundsChanged: debounce(function () {
            this.onBoundsChanged();
        }, 300),
        /* Available SlotProp Methods */
        showBoundary(show) {
            if (show !== undefined) {
                this.boundaryDisabled = !show;
            } else {
                this.boundaryDisabled = !this.boundaryDisabled;
            }
            // Clear drawing regardless
            this.clearDrawing();
        },
        enableDrawing(enabled) {
            if (enabled !== undefined) {
                this.freeDrawEnabled = enabled;
            } else {
                this.freeDrawEnabled = !this.freeDrawEnabled;
            }
        },
        clearDrawing() {
            this.drawnBounds = null;
            this.drawnPath = [];
        },
        changeMapLayer(layer) {
            this.activeLayer = layer;
        },
        changeMapType(type) {
            if (typeof type === 'number') {
                this.activeMapType = mapTypesDict[type] || 'activeMap';
            } else {
                this.activeMapType = type;
            }
        },
        setStreetView(coordinates = null) {
            const api = this.$refs.mapRef?.api;
            const gmap = this.$refs.mapRef?.map;

            if (!api || !gmap) {
                console.warn('Google Maps API or map instance not initialized.');
                return;
            }

            const streetViewService = new api.StreetViewService();
            const streetView = gmap.getStreetView?.();

            if (!streetView) {
                console.warn('Street View is not available from this map instance.');
                return;
            }

            if (coordinates) {
                let { lat, lng } = coordinates;

                if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
                    console.error('Invalid coordinates:', coordinates);
                    return;
                }

                const position = new api.LatLng(lat, lng);

                streetViewService.getPanorama({ location: position, radius: 50 }, (data, status) => {
                    if (status !== api.StreetViewStatus.OK || !data?.location?.pano) {
                        console.warn('No Street View data available for this location:', coordinates);
                        streetView.setVisible(false);
                        return;
                    }

                    try {
                        this.streetViewRef = streetView;

                        // Prefer setPano for exact pano rendering
                        this.streetViewRef.setPano(data.location.pano);

                        this.streetViewRef.setMotionTracking(false);
                        this.streetViewRef.setOptions({
                            addressControl: false,
                            linksControl: false,
                            panControl: false,
                            enableCloseButton: false,
                            zoomControl: false,
                            fullscreenControl: false,
                        });

                        this.streetViewRef.setVisible(true);
                    } catch (error) {
                        console.error('Error showing Street View:', error);
                    }
                });
            } else {
                if (this.streetViewRef) {
                    try {
                        this.streetViewRef.setVisible(false);
                        this.streetViewRef = null;
                    } catch (error) {
                        console.error('Error hiding Street View:', error);
                    }
                }
            }
        },
        unhoverMarker() {
            document.querySelectorAll('.pin-hover').forEach((e) => e.classList.remove('pin-hover'));
        },
        hoverMarker(type, lat, lng, autoUnselect) {
            if (document.querySelectorAll('.pin-hover').length > 0) {
                document.querySelectorAll('.pin-hover').forEach((e) => e.classList.remove('pin-hover'));
                if (autoUnselect) {
                    document.querySelectorAll('.pin-selected').forEach((e) => e.classList.remove('pin-selected'));
                }
            }
            if (type === pinType.poi) {
                this.$el.querySelector(`[data-key="${pinType.poi}_${lat}_${lng}"]`)?.classList.add('pin-hover');
            } else if (type === pinType.school) {
                this.$el.querySelector(`[data-key="${pinType.school}_${lat}_${lng}"]`)?.classList.add('pin-hover');
            } else if (type === pinType.property) {
                this.$el.querySelector(`[data-key="${pinType.property}_${lat}_${lng}"]`)?.classList.add('pin-hover');
            }
        },
        /* Events */
        onMarkerUnselect(data, type) {
            this.$emit('markerUnselect', data, type);
            this.toggleDrag(false);
        },
        onMarkerSelected(data, type) {
            this.$emit('markerSelected', data, type);
            requestAnimationFrame(() => {
                this.toggleDrag(data?.length > 1);
            });
        },
        toggleDrag(disable) {
            if (this.hasTouch) {
                if (disable === true) {
                    this.draggable = false;
                } else {
                    this.draggable = true;
                }
            }
        },
    },
    mounted() {
        this.isMounted = true;
        this.fetchBoundaryPath();
        this.$eventBus.$on('searchMap.resetMap', () => {
            this.clearDrawing();
            this.resetMapPosition();
        });
    },
    watch: {
        activeLayer(layer) {
            const api = this.$refs.mapRef.api;

            if (this.gmapLayerRef) {
                this.gmapLayerRef.setMap(null);
            }

            switch (layer) {
                case 'transit':
                    this.gmapLayerRef = new api.TransitLayer();
                    this.gmapLayerRef.setMap(this.$refs.mapRef.map);
                    break;
                case 'traffic':
                    this.gmapLayerRef = new api.TrafficLayer();
                    this.gmapLayerRef.setMap(this.$refs.mapRef.map);
                    break;
                case 'biking':
                    this.gmapLayerRef = new api.BicyclingLayer();
                    this.gmapLayerRef.setMap(this.$refs.mapRef.map);
                    break;
            }
        },
        isMapReady(ready) {
            if (ready) {
                this.initializeMapStyles();
                this.$emit('ready', this.$refs.mapRef);
            }
        },
        isInitialBoundaryReady(ready) {
            /*
                Fit the map to the appropriate coordinates,
                 boundaries or visible listings
            */
            if (ready) {
                this.resetMapPosition();
            }
        },
        boundaryDisabled() {
            if (this.isInitialBoundaryReady && !this.disableMapChangeEvent) {
                this.debouncedOnBoundsChanged();
            }
        },
        freeDrawEnabled(enabled) {
            if (enabled) {
                this.createDrawingManager();
            } else {
                this.destroyDrawingManager();
            }
        },
        center(newValue) {
            if (newValue && newValue.lat && newValue.lng) {
                this.resetMapPosition();
            }
        },
        boundaryIndexId: {
            immediate: true,
            handler(newValue, oldValue) {
                if (newValue !== undefined && oldValue !== undefined && newValue !== oldValue) {
                    if (newValue === '') {
                        this.boundaryPath = [];
                        // If no location provided, we still need to reset map
                        !this.center && this.resetMapPosition();
                    } else {
                        this.fetchBoundaryPath();
                    }
                }
            },
        },
    },
    beforeMount() {
        this.hasTouch = 'ontouchstart' in window;
        this.apiPromise = loadGMap();
    },
    beforeUnmount() {
        this.$eventBus.off('searchMap.resetMap');
    },
};
